import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { aIsMapEasing, useSetAtom } from '~/atoms'
import {
  DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
  type AppMapCameraOptions,
  type AppMapRef,
} from '~/components'

export function useMapEaseTo(mapRef: RefObject<AppMapRef | null>) {
  const setIsMapEasing = useSetAtom(aIsMapEasing)
  const easingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearEasingTimeout = useCallback(() => {
    if (easingTimeoutRef.current === null) return

    clearTimeout(easingTimeoutRef.current)
    easingTimeoutRef.current = null
  }, [])

  useEffect(() => clearEasingTimeout, [clearEasingTimeout])

  const mapEaseTo = useCallback(
    (easeToOptions: AppMapCameraOptions) => {
      const map = mapRef.current

      if (!map) {
        return
      }

      const options = {
        duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
        ...easeToOptions,
      }

      setIsMapEasing(true)
      clearEasingTimeout()

      if (map.easeTo) {
        map.easeTo(options)
      } else if (map.moveCamera) {
        map.moveCamera({
          center: Array.isArray(options.center)
            ? {
                lat: options.center[1],
                lng: options.center[0],
              }
            : options.center,
          heading: options.heading ?? options.bearing,
          tilt: options.tilt ?? options.pitch,
          zoom: options.zoom,
        })
      }

      easingTimeoutRef.current = setTimeout(() => {
        easingTimeoutRef.current = null
        setIsMapEasing(false)
      }, options.duration + 100)
    },
    [clearEasingTimeout, mapRef, setIsMapEasing]
  )
  return mapEaseTo
}
